import {
    CheshireRecordType,
    CheshireRecordSubType
} from "./CheshireRecordTypes";
import { IStyleFactory } from "@esdm/map/dist/layers/vector/styles/IStyleFactory";
import Style, { StyleFunction } from "ol/style/Style";
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import RegularShape from 'ol/style/RegularShape';
import Feature from 'ol/Feature';
import * as olHas from 'ol/has';

export class HbsmrStyleFactory implements IStyleFactory {

    public getStyle(recordType: string, recordSubType: string = null): Style | Style[] | StyleFunction
    {
        let style: Style | Style[] | StyleFunction = null;

        //try and resolve sub type first
        if (recordSubType !== null) {
            switch (recordSubType) {
                case CheshireRecordSubType.AreaArchaeologicalImportance:
                    style = this.AreaArchaeologicalImportance();
                    break;

                case CheshireRecordSubType.AreaArchaeologicalPotential:
                    style = this.AreaArchaeologicalPotential();
                    break;

                case CheshireRecordSubType.AreaSpecialArchaeologicalPotential:
                    style = this.AreaSpecialArchaeologicalPotential();
                    break;

                case CheshireRecordSubType.ConservationArea:
                    style = this.ConservationArea();
                    break;

                case CheshireRecordSubType.ListedBuilding:
                    style = this.ListedBuilding();
                    break;

                case CheshireRecordSubType.LocallyListedBuilding:
                    style = this.LocallyListedBuilding();
                    break;

                case CheshireRecordSubType.RegisteredBattlefields:
                    style = this.RegisteredBattlefields();
                    break;

                case CheshireRecordSubType.RegisteredParksAndGardens:
                    style = this.RegisteredParksAndGardens();
                    break;

                case CheshireRecordSubType.ScheduledMonument:
                    style = this.ScheduledMonument();
                    break;

                case CheshireRecordSubType.WorldHeritageSite:
                    style = this.WHS();
                    break;

                case CheshireRecordSubType.UADCharacterZones:
                    style = this.UADCharacterZones();
                    break;

            }

        }

        if (style === null) {
            switch (recordType) {
                case CheshireRecordType.Monument:
                    style = this.Monument();
                    break;

                case CheshireRecordType.Event:
                    style = this.Event();
                    break;

                case CheshireRecordType.Designation:
                    style = this.Designation();
                    break;
            }
        }

        return style;
    }

    public getSelectedStyle(recordType: CheshireRecordType): Style | Style[] | StyleFunction {
        let style: Style | Style[] | StyleFunction = null;

        switch (recordType) {
            case CheshireRecordType.Monument:
                style = this.MonumentSelected();
                break;

            case CheshireRecordType.Event:
                style = this.EventSelected();
                break;

            case CheshireRecordType.Designation:
                style = this.DesignationSelected();
                break;
        }

        return style;
    }

    private CommonStyle(fillColour: string): Style[] {
        let fill = new Fill({
            color: fillColour
        });
        let stroke = new Stroke({
            color: 'rgba(255,255,255,1)',
            width: 3
        });

        return [
            new Style({
                image: new Circle({
                    fill: fill,
                    stroke: stroke,
                    radius: 12
                }),
                fill: fill,
                stroke: stroke
            })
        ];
    }


    private Monument(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case "Point":
                    return new Style({
                        text: new Text({
                            text: '\uf2ec',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(227, 26, 28, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(227, 26, 28, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(227, 26, 28, 1)' })
                    });
            }
        };
    }

    private Event(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case "Point":
                    return new Style({
                        text: new Text({
                            text: '\uf2ec',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(255, 127, 0, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(255, 127, 0, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(255, 127, 0, 1)', width: 2 })
                    });
            }
        };
    }

    private Designation(): Style[] {
        return this.CommonStyle('rgba(132, 71, 15,0.4)');
    }

    private AreaArchaeologicalImportance(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case "Point":
                    return new Style({
                        text: new Text({
                            text: '\uf005',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(251, 255, 13, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(251, 255, 13, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(251, 255, 13, 1)', width: 2 })
                    });
            }
        };
    }

    private AreaArchaeologicalPotential(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case "Point":
                    return new Style({
                        text: new Text({
                            text: '\uf005',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(255, 216, 0, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(255, 216, 0, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(255, 216, 0, 1)', width: 2 })
                    });
            }
        };
    }

    private AreaSpecialArchaeologicalPotential(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case "Point":
                    return new Style({
                        text: new Text({
                            text: '\uf005',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(253, 191, 111, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(253, 191, 111, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(253, 191, 111, 1)', width: 2 })
                    });
            }
        };
    }

    private ConservationArea(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case "Point":
                    return new Style({
                        text: new Text({
                            text: '\uf219',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(166, 81, 227, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(166, 81, 227, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(166, 81, 227, 1)', width:2 })
                    });
            }
        };
    }

    private ListedBuilding(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case "Point":
                    return new Style({
                        text: new Text({
                            text: '\uf0c8',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(34, 148, 224, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(34, 148, 224, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(34, 148, 224, 1)', width: 2 })
                    });
            }
        };
    }

    private LocallyListedBuilding(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case "Point":
                    return new Style({
                        text: new Text({
                            text: '\uf0c8',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(99, 90, 22, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(99, 90, 22, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(99, 90, 22, 1)', width: 2 })
                    });
            }
        };
    }

    private RegisteredBattlefields(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case "Point":
                    return new Style({
                        text: new Text({
                            text: '\uf219',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(141, 90, 153, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(141, 90, 153, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(141, 90, 153, 1)' })
                    });
            }
        };
    }

    private RegisteredParksAndGardens(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case "Point":
                    return new Style({
                        text: new Text({
                            text: '\uf219',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(84, 176, 74, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(84, 176, 74, 0.5)' }),
                        stroke: new Stroke({ color: 'rgba(84, 176, 74, 1)' })
                    });
            }
        };
    }

    private ScheduledMonument(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case "Point":
                    return new Style({
                        text: new Text({
                            text: '\uf2ec',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            rotation: Math.PI,
                            fill: new Fill({ color: 'rgba(72, 123, 182, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(72, 123, 182, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(72, 123, 182, 1)' })
                    });
            }
        };
    }

    private WHS(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case "Point":
                    return new Style({
                        text: new Text({
                            text: '\uf219',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(187, 185, 208, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(187, 185, 208, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(187, 185, 208, 1)' })
                    });
            }
        };
    }

    private UADCharacterZones(): StyleFunction {
        return function (feature: Feature): Style {
            let significance = feature.get('significance');
            let fill = new Fill({ color: 'rgba(0, 0, 0, 0.4)' });
            let stroke = new Stroke({ color: 'rgba(0, 0, 0, 1)', width: 1 });
            let colour = 'rgba(0, 0, 0, 1)';
            switch (significance) {
                case "Local": {
                    colour = 'rgba(197, 226, 99, 1)';
                    fill = new Fill({ color: 'rgba(197, 226, 99, 0.6)' });
                    break;
                }
                case "Regional": {
                    colour = 'rgba(126, 145, 63, 1)';
                    fill = new Fill({ color: 'rgba(240, 222, 16, 0.6)' });
                    break;
                }
                case "National": {
                    colour = 'rgba(65, 86, 14, 1)';
                    fill = new Fill({ color: 'rgba(227,26,28, 0.6)' });
                    break;
                }
            }
                    
            return new Style({
                    image: new Circle({
                        fill: new Fill({color: colour}),
                        stroke: new Stroke({ color: 'rgba(50,50,50, 1)' }),
                        radius: 6
                    }),
                    fill: fill,
                    stroke: stroke
                });
        };
    }
    
    private Selected(): Style[] {
        var fill = new Fill({
            color: 'rgba(255, 255, 0, 0.6)'
        });

        return [
            new Style({
                image: new Circle({
                    fill: fill,
                    stroke: new Stroke({
                        color: 'rgba(255, 173, 10, 1)',
                        width: 3
                    }),
                    radius: 10
                }),
                fill: fill,
                stroke: new Stroke({
                    color: 'rgba(255, 173, 10, 1)',
                    width: 3
                })
            })
        ];
    }
    
    private MonumentSelected(): Style[] {
        return this.Selected();
    }

    private EventSelected(): Style[] {
        return this.Selected();
    }

    private DesignationSelected(): Style[] {
        return this.Selected();
    }
}
